/**
 * Authorization-code + PKCE against the Keycloak realm, hand-written on `fetch`
 * (ADR-AUTH-04). Public client `onto-frontend`, no client secret in the SPA.
 *
 * Why no library: the whole surface is three requests (authorize redirect, token
 * exchange, refresh) and every OIDC library we looked at defaults to persisting
 * the tokens in `sessionStorage`/`localStorage` — the exact thing this repo
 * forbids. Keeping it here means the storage policy is 40 lines we can read,
 * not a configuration flag that a version bump can change under us.
 */

import { env } from '@/config/env';
import { deriveCodeChallenge, randomUrlSafeString, saveTransaction, takeTransaction } from './pkce';

/** A sign-in failure, flagged when it came from a silent (prompt=none) attempt. */
export class SignInError extends Error {
  /**
   * `returnTo` is carried on the ERROR, and that is the fix for a bug that shipped for weeks.
   *
   * A failed silent attempt consumes the transaction, so the route the user originally asked for used
   * to die right here: the callback sent them to `/auth`, the interactive login then recorded
   * `returnTo=/auth`, and the callback refused that (it must never bounce anyone back into the auth
   * routes) and fell back to `/`. Measured 2026-08-19: every deep link — a bookmark, a link in a
   * report, an e2e spec — silently became the home page.
   */
  constructor(
    message: string,
    public readonly silent: boolean,
    public readonly returnTo?: string
  ) {
    super(message);
    this.name = 'SignInError';
  }
}

export interface TokenSet {
  accessToken: string;
  refreshToken: string | null;
  /** Absolute epoch ms at which the access token expires. */
  expiresAt: number;
}

const authorizeEndpoint = (): string => `${env.keycloakIssuer}/protocol/openid-connect/auth`;
const tokenEndpoint = (): string => `${env.keycloakIssuer}/protocol/openid-connect/token`;
const logoutEndpoint = (): string => `${env.keycloakIssuer}/protocol/openid-connect/logout`;

export function isConfigured(): boolean {
  return Boolean(env.keycloakIssuer && env.keycloakClientId);
}

/** The redirect URI registered on the client: this origin + /auth/callback. */
export function redirectUri(): string {
  return env.keycloakRedirectUri || `${window.location.origin}/auth/callback`;
}

/**
 * Start the flow: mint a verifier, remember it, leave for Keycloak. Never
 * returns (the browser navigates away).
 *
 * `silent` adds `prompt=none`: Keycloak either returns a code straight away
 * because the SSO session is alive (the reload case, and the e2e case), or
 * answers `error=login_required` without ever showing a form. It is the reason
 * a reload does not cost a login even though no token is persisted.
 */
export async function beginLogin(
  returnTo: string = window.location.pathname,
  options: { silent?: boolean } = {}
): Promise<void> {
  const codeVerifier = randomUrlSafeString();
  const state = randomUrlSafeString(16);
  saveTransaction({ codeVerifier, state, returnTo, silent: options.silent === true });

  const params = new URLSearchParams({
    client_id: env.keycloakClientId,
    response_type: 'code',
    scope: env.keycloakScope,
    redirect_uri: redirectUri(),
    state,
    code_challenge: await deriveCodeChallenge(codeVerifier),
    code_challenge_method: 'S256',
  });
  if (options.silent) params.set('prompt', 'none');
  window.location.assign(`${authorizeEndpoint()}?${params.toString()}`);
}

async function postToken(body: URLSearchParams): Promise<TokenSet> {
  const response = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  const payload = (await response.json().catch(() => null)) as
    | { access_token?: string; refresh_token?: string; expires_in?: number; error_description?: string; error?: string }
    | null;
  if (!response.ok || !payload?.access_token) {
    const detail = payload?.error_description || payload?.error || `HTTP ${response.status}`;
    throw new Error(`Keycloak token request failed: ${detail}`);
  }
  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token ?? null,
    // Keycloak reports 300 s here; treat a missing value as already expiring.
    expiresAt: Date.now() + (payload.expires_in ?? 0) * 1000,
  };
}

/**
 * Finish the flow from the `?code=&state=` the callback route received.
 * Throws on a state mismatch, a missing transaction, or a rejected exchange.
 */
export async function completeLogin(
  search: URLSearchParams
): Promise<{ tokens: TokenSet; returnTo: string }> {
  const error = search.get('error');
  if (error) {
    const failed = takeTransaction();
    throw new SignInError(
      search.get('error_description') || error,
      failed?.silent === true,
      failed?.returnTo
    );
  }
  const code = search.get('code');
  const state = search.get('state');
  const tx = takeTransaction();
  if (!code || !tx) throw new SignInError('No pending sign-in to complete.', false);
  if (state !== tx.state) {
    throw new SignInError(
      'Sign-in state mismatch — the request was not started here.',
      tx.silent === true,
      tx.returnTo
    );
  }

  const tokens = await postToken(
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: env.keycloakClientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: tx.codeVerifier,
    })
  );
  return { tokens, returnTo: tx.returnTo };
}

/** Exchange a refresh token for a fresh access token (300 s lifetime). */
export async function refreshTokens(refreshToken: string): Promise<TokenSet> {
  const tokens = await postToken(
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: env.keycloakClientId,
      refresh_token: refreshToken,
    })
  );
  // Keycloak rotates the refresh token (use_refresh_tokens=true); if a response
  // ever omits it, keep the previous one rather than losing the session.
  return { ...tokens, refreshToken: tokens.refreshToken ?? refreshToken };
}

/** End the Keycloak SSO session too, so the next login really asks. */
export function endSessionUrl(): string {
  const params = new URLSearchParams({
    client_id: env.keycloakClientId,
    post_logout_redirect_uri: `${window.location.origin}/auth`,
  });
  return `${logoutEndpoint()}?${params.toString()}`;
}
